import {Linking, Platform} from 'react-native';
import {
  CommonActions,
  getActionFromState,
  getPathFromState,
  getStateFromPath,
} from '@react-navigation/native';

import config from '../config';
import {AppRoute} from '../models/AppRoute';
import {navigationRef} from '../navigation/RootNavigation';

import {logError} from './errorLogging';

/**
 * Domains that belong to this app. URLs matching these should navigate
 * in-app instead of opening the external browser.
 */
const APP_DOMAINS = [
  config.HOST_DOMAIN,
  `beta.${config.HOST_DOMAIN}`,
  `${config.WHITE_LABEL_ID}-brand-dev.lmg-bw.au`,
];

/**
 * Extracts the path from a URL if it matches one of the app's own domains.
 * Returns null for external URLs.
 */
const extractInternalPath = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (APP_DOMAINS.some(d => parsed.hostname === d)) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    // Not a valid URL
  }
  return null;
};

/**
 * ROOT and ROOT_STORE_SELECTION_FLOW are conditionally rendered in App.tsx —
 * only one exists at a time. Paths under /welcome/* resolve to
 * ROOT_STORE_SELECTION_FLOW, but when a store is selected that navigator
 * doesn't exist. Remap the path so it resolves to the mounted navigator.
 */
const remapConditionalPath = (path: string): string => {
  if (!navigationRef?.isReady()) return path;

  const rootState = navigationRef.getRootState();
  const mountedRouteNames = rootState?.routeNames ?? [];

  const hasRoot = mountedRouteNames.includes(AppRoute.ROOT);
  const hasStoreSelection = mountedRouteNames.includes(AppRoute.ROOT_STORE_SELECTION_FLOW);

  if (hasRoot && !hasStoreSelection && path.startsWith('/welcome/')) {
    // Store is selected → ROOT is mounted. Strip /welcome/ prefix so the
    // path resolves under ROOT > MAIN instead of ROOT_STORE_SELECTION_FLOW.
    return path.replace('/welcome/', '/');
  }

  if (hasStoreSelection && !hasRoot && !path.startsWith('/welcome/')) {
    // No store selected → ROOT_STORE_SELECTION_FLOW is mounted.
    // Paths like /find-suburb should be prefixed with /welcome/.
    const storeSelectionPaths = ['find-suburb', 'choose-store'];
    const segment = path.replace(/^\//, '').split('/')[0];
    if (storeSelectionPaths.includes(segment)) {
      return `/welcome${path}`;
    }
  }

  return path;
};

/**
 * Params carried by a screen reached through a same-route in-page link.
 * `linkTrail` holds the paths of the screens the user came from, oldest first.
 */
export type LinkTrailParams = {linkTrail?: string[]};

/** Depth cap — a trail this long is a link loop, not a reading session. */
const MAX_LINK_TRAIL = 10;

type RouteLike = {name: string; params?: object; state?: StateLike};
type StateLike = {index?: number; routes: RouteLike[]};

/** Deepest focused route of a (partial) navigation state. */
const getLeafRoute = (state: StateLike): RouteLike | undefined => {
  let route: RouteLike | undefined = state.routes?.[state.index ?? state.routes.length - 1];
  while (route?.state?.routes?.length) {
    const child: StateLike = route.state;
    route = child.routes[child.index ?? child.routes.length - 1];
  }
  return route;
};

/**
 * ARTICLE / PROMOTION / PRODUCT are screens of the bottom-tab navigator, and
 * `TabRouter` holds exactly one route per name: navigating ARTICLE -> ARTICLE
 * swaps params on the existing route key, so the tab history never grows and
 * the header back button walks straight past the article the user came from.
 * (Making `getId` param-aware does not help — the router then replaces the
 * route key, leaving the history entry pointing at a key that no longer
 * exists, and GO_BACK dead-ends.)
 *
 * So record the origin path on the destination's params. `BrandHeader` and
 * `useLinkTrailBack` step back through it. The trail is self-clearing:
 * React Navigation v7 `navigate` replaces params, so entering the screen
 * fresh from a list carries no trail.
 */
const applyLinkTrail = (
  state: StateLike,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navStructure: any,
  trailOverride?: string[],
): void => {
  const leaf = getLeafRoute(state);
  if (!leaf) return;

  // Stepping BACK through a trail: the caller owns the remaining trail.
  if (trailOverride) {
    if (trailOverride.length) leaf.params = {...leaf.params, linkTrail: trailOverride};
    return;
  }

  try {
    const current = navigationRef.getCurrentRoute();
    if (!current || current.name !== leaf.name) return;

    const currentPath = getPathFromState(navigationRef.getRootState(), navStructure);
    const previous = (current.params as LinkTrailParams | undefined)?.linkTrail ?? [];
    leaf.params = {
      ...leaf.params,
      linkTrail: [...previous, currentPath].slice(-MAX_LINK_TRAIL),
    };
  } catch (e) {
    // Path could not be derived (a route param without a `stringify`) — fall
    // back to the pre-trail behaviour rather than blocking the navigation.
    logError(e, 'openPageLink.linkTrail');
  }
};

/**
 * Navigate to a page link. If the URL matches the app's own domain,
 * navigates in-app using React Navigation. Otherwise opens the external browser.
 *
 * Handles:
 * - Relative paths: `/promotions/10k-pay-day` → in-app
 * - Own-domain URLs: `https://sipnsave.com.au/promotions/10k-pay-day` → in-app
 * - External URLs: `https://example.com/...` → browser
 *
 * Stack semantics: in-app navigation dispatches a NAVIGATE action (via
 * `getActionFromState`), which preserves the existing back-stack so the user
 * can return to the previous screen via the header back button. If the target
 * route already exists in the stack, NAVIGATE pops back to it (intermediate
 * routes unmount). A full `CommonActions.reset` is used only as a fallback
 * when no action can be derived (cold-start deep links with no current state).
 *
 * Do NOT use this helper for flows that need to wipe history (e.g. logout
 * → fresh root). Use `navigationRef.dispatch(CommonActions.reset(...))`
 * directly for those.
 */
export const openPageLink = async (
  pageLink: string,
  /** Remaining trail when stepping BACK through a link trail. */
  trailOverride?: string[],
): Promise<void> => {
  if (!pageLink) return;

  // Determine the path for in-app navigation
  let internalPath: string | null = null;

  if (pageLink.startsWith('/')) {
    internalPath = pageLink;
  } else if (pageLink.startsWith('http')) {
    internalPath = extractInternalPath(pageLink);
  }

  if (internalPath && navigationRef?.isReady()) {
    try {
      // Remap paths that target conditionally-mounted navigators
      const resolvedPath = remapConditionalPath(internalPath);

      // Lazy-require navStructure to avoid circular dependency
      const {navStructure} = require('../navigation/linking');
      const state = getStateFromPath(resolvedPath, navStructure) as StateLike | undefined;

      if (state) {
        // Same-route in-page link (e.g. article -> article): stamp the origin
        // path so the back button can return to it.
        applyLinkTrail(state, navStructure, trailOverride);

        // Prefer a NAVIGATE action so the existing back-stack is preserved
        // (banner taps from HOME should land on a screen the user can return
        // FROM via the header back button — DIS-1180). Fall back to a full
        // reset whenever the state cannot be reduced to a navigate action
        // (e.g. cold-start deep-links with no current state to merge into,
        // or a state with no resolvable leaf route).
        const action = getActionFromState(
          state as Parameters<typeof getActionFromState>[0],
          navStructure,
        );
        if (action) {
          navigationRef.dispatch(action);
        } else {
          navigationRef.dispatch(
            CommonActions.reset(state as Parameters<typeof CommonActions.reset>[0]),
          );
        }
        return;
      }
    } catch (e) {
      logError(e, 'openPageLink.navigate');
    }

    // Fallback for web: same-origin navigation (reload but stays in tab)
    if (Platform.OS === 'web') {
      window.location.href = internalPath;
      return;
    }
  }

  // External URLs — open in browser (new tab on web, Safari on native)
  try {
    if (await Linking.canOpenURL(pageLink)) {
      await Linking.openURL(pageLink);
    }
  } catch (e) {
    logError(e, 'openPageLink');
  }
};

/** Non-empty link trail carried by a route's params, if any. */
export const getLinkTrail = (params: unknown): string[] | undefined => {
  const trail = (params as LinkTrailParams | undefined)?.linkTrail;
  return trail?.length ? trail : undefined;
};

/**
 * Step one entry back through a link trail: navigate to the previous path,
 * handing it the rest of the trail so the next back press continues the chain.
 */
export const goBackThroughLinkTrail = (trail: string[]): void => {
  openPageLink(trail[trail.length - 1], trail.slice(0, -1));
};
